import { ComponentProps, ReactNode, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Box, Tab, Tabs } from '@mui/material';

type TabsProps = ComponentProps<typeof Tabs>;

const getTabValue = (tabName: string) =>
  tabName
    .split(' ')
    .map((t) => t.toLowerCase())
    .join('-');

export function TabList({
  defaultTab,
  queryParam = 'activeTab',
  tabs,
  ...tabsProps
}: TabsProps & {
  defaultTab: string;
  queryParam?: string;
  tabs: Array<{
    label: string;
    component: ReactNode;
  }>;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [value, setValue] = useState(
    searchParams.get(queryParam) || getTabValue(defaultTab)
  );

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    event.preventDefault();
    if (!tabs.map(({ label }) => getTabValue(label)).includes(newValue)) {
      return;
    }
    setValue(newValue);
    setSearchParams((prevParams) => ({
      ...prevParams,
      [queryParam]: newValue,
    }));
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: tabsProps.orientation === 'vertical' ? 'row' : 'column',
        width: '100%',
      }}
    >
      <Tabs
        textColor="primary"
        indicatorColor="primary"
        onChange={handleChange}
        value={value}
        {...tabsProps}
      >
        {tabs.map(({ label }) => (
          <Tab key={label} label={label} value={getTabValue(label)} />
        ))}
      </Tabs>
      {tabs.map(({ component, label }) => (
        <TabPanel
          key={label}
          value={getTabValue(label)}
          curTab={value}
          orientation={tabsProps.orientation}
        >
          {component}
        </TabPanel>
      ))}
    </Box>
  );
}

const TabPanel = ({
  children,
  value,
  curTab,
  orientation,
  ...other
}: Pick<TabsProps, 'orientation'> & {
  children?: ReactNode;
  curTab: string;
  value: string;
}) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== curTab}
      style={orientation === 'vertical' ? {} : {}}
      {...other}
    >
      {value === curTab && <>{children}</>}
    </div>
  );
};
